/**
 * Vincent #331/#332/#334 (Eric 2026-06-17) — Dropdown de tri réutilisable.
 *
 * Options exigées par Vincent (mêmes 6 valeurs partout) :
 *   - alphabétique (A–Z)
 *   - alphabétique (Z–A)
 *   - date d'inscription (croissant)
 *   - date d'inscription (décroissant)
 *   - dernier accès (croissant)
 *   - dernier accès (décroissant)
 *
 * Utilisation côté page :
 *   const [sortBy, setSortBy] = useState("alpha_asc");
 *   const sorted = useMemo(() => sortItems(items, sortBy, getters), [items, sortBy]);
 *   <SortDropdown value={sortBy} onChange={setSortBy} testId="members-sort" />
 *
 * Le 2e argument `getters` permet à chaque page de fournir ses accesseurs
 * pour `name`, `created_at`, `last_active` (les champs varient légèrement).
 */
import { useLang } from "../../contexts/LanguageContext";

export const SORT_OPTIONS = [
  { value: "alpha_asc",       fr: "nom (A–Z)",                           en: "name (A–Z)" },
  { value: "alpha_desc",      fr: "nom (Z–A)",                           en: "name (Z–A)" },
  { value: "email_asc",       fr: "courriel (A–Z)",                      en: "email (A–Z)" },
  { value: "email_desc",      fr: "courriel (Z–A)",                      en: "email (Z–A)" },
  { value: "created_asc",     fr: "date d'inscription (croissant)",      en: "registration date (ascending)" },
  { value: "created_desc",    fr: "date d'inscription (décroissant)",    en: "registration date (descending)" },
  { value: "last_seen_asc",   fr: "dernier accès (croissant)",           en: "last access (ascending)" },
  { value: "last_seen_desc",  fr: "dernier accès (décroissant)",         en: "last access (descending)" },
];

export const DEFAULT_GETTERS = {
  name: (item) => (item?.name || item?.full_name || item?.email || "").toLowerCase(),
  email: (item) => (item?.email || "").toLowerCase(),
  created_at: (item) => item?.created_at || item?.registered_at || item?.joined_at || "",
  last_active: (item) => item?.last_active || item?.last_seen_at || item?.last_login || "",
};

export function sortItems(items, sortBy, getters = DEFAULT_GETTERS) {
  if (!Array.isArray(items)) return [];
  const arr = [...items];
  const cmp = (a, b) => {
    switch (sortBy) {
      case "alpha_asc":      return getters.name(a).localeCompare(getters.name(b));
      case "alpha_desc":     return getters.name(b).localeCompare(getters.name(a));
      case "email_asc":      return getters.email(a).localeCompare(getters.email(b));
      case "email_desc":     return getters.email(b).localeCompare(getters.email(a));
      case "created_asc":    return String(getters.created_at(a)).localeCompare(String(getters.created_at(b)));
      case "created_desc":   return String(getters.created_at(b)).localeCompare(String(getters.created_at(a)));
      case "last_seen_asc":  return String(getters.last_active(a)).localeCompare(String(getters.last_active(b)));
      case "last_seen_desc": return String(getters.last_active(b)).localeCompare(String(getters.last_active(a)));
      default:               return 0;
    }
  };
  return arr.sort(cmp);
}

export default function SortDropdown({ value, onChange, testId = "sort-dropdown", className = "" }) {
  const { lang } = useLang();
  const label = lang === "fr" ? "Trier par" : "Sort by";
  return (
    <label className={`inline-flex items-center gap-2 text-xs text-zinc-600 ${className}`}>
      <span className="font-medium">{label} :</span>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-zinc-300 rounded px-2 py-1 text-xs bg-white hover:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
      >
        {SORT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {lang === "fr" ? opt.fr : opt.en}
          </option>
        ))}
      </select>
    </label>
  );
}
